import { useEffect, useMemo, useRef, useState } from "react";
import Icon from "./Icon";

// Searchable multi-select dropdown. options: [{ id, label, hint? }].
// `value` is an array of option ids; [] means "no filter". Sibling of
// SearchableSelect — same popover chrome, but rows toggle a checkbox and the
// list stays open so several options can be ticked in one go.
export default function MultiSelect({
  value = [],
  onChange,
  options = [],
  placeholder = "All",
  searchPlaceholder = "Search…",
  width = 220,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Ids arrive as numbers from the API but round-trip through the URL as
  // strings, so every comparison goes through String().
  const selectedKeys = useMemo(() => new Set(value.map((v) => String(v))), [value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const hay = `${o.label || ""} ${o.hint || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [options, query]);

  // "Nothing" → placeholder, one pick → its label, more → a count.
  const summary = useMemo(() => {
    if (value.length === 0) return placeholder;
    if (value.length === 1) {
      const only = options.find((o) => String(o.id) === String(value[0]));
      return only ? only.label : "1 selected";
    }
    return `${value.length} selected`;
  }, [value, options, placeholder]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    inputRef.current?.focus();
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = (id) => {
    const key = String(id);
    onChange(
      selectedKeys.has(key)
        ? value.filter((v) => String(v) !== key)
        : [...value, id],
    );
  };

  // Select-all acts on what's currently visible (i.e. the search results),
  // unioned with anything already picked but filtered out of view.
  const selectAllVisible = () => {
    const merged = [...value];
    filtered.forEach((o) => {
      if (!selectedKeys.has(String(o.id))) merged.push(o.id);
    });
    onChange(merged);
  };

  return (
    <div ref={wrapRef} style={{ position: "relative", width }}>
      <button
        type="button"
        className="select"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          gap: 6,
          paddingRight: 10,
          // `.select` paints its own chevron via background-image; we render an
          // Icon instead so the caret sits after the clear-X.
          backgroundImage: "none",
          color: value.length ? "var(--ink-900)" : "var(--ink-500)",
        }}
      >
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {summary}
        </span>
        {value.length > 0 && (
          <span
            role="button"
            tabIndex={0}
            title="Clear"
            onClick={(e) => { e.stopPropagation(); onChange([]); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onChange([]); } }}
            style={{ color: "var(--ink-400)", display: "inline-flex" }}
          >
            <Icon name="x" size={12} />
          </span>
        )}
        <Icon name="chevron-down" size={12} style={{ color: "var(--ink-400)" }} />
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            width: "100%",
            minWidth: "min(220px, calc(100vw - 32px))",
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 7,
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
            zIndex: 100,
            display: "flex",
            flexDirection: "column",
            maxHeight: 340,
          }}
        >
          <div style={{ padding: 6, borderBottom: "1px solid var(--border)", position: "relative" }}>
            <Icon
              name="search"
              size={12}
              style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "var(--ink-400)", pointerEvents: "none" }}
            />
            <input
              ref={inputRef}
              className="input"
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ paddingLeft: 28, fontSize: 12, height: 30 }}
            />
          </div>

          <div style={{ overflowY: "auto", maxHeight: 220 }}>
            {filtered.length === 0 ? (
              <div style={{ padding: 14, fontSize: 12, color: "var(--ink-500)", textAlign: "center" }}>
                No matches
              </div>
            ) : (
              filtered.map((o) => {
                const active = selectedKeys.has(String(o.id));
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => toggle(o.id)}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      padding: "8px 12px",
                      fontSize: 13,
                      background: active ? "var(--primary-50)" : "transparent",
                      color: active ? "var(--primary-800)" : "var(--ink-800)",
                      borderTop: "1px solid var(--border)",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      readOnly
                      tabIndex={-1}
                      style={{ pointerEvents: "none", flexShrink: 0 }}
                    />
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {o.label}
                    </span>
                    {o.hint && (
                      <span className="t-muted text-xs" style={{ flexShrink: 0 }}>{o.hint}</span>
                    )}
                  </button>
                );
              })
            )}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 10px",
              borderTop: "1px solid var(--border)",
              background: "var(--surface-2)",
            }}
          >
            <span className="t-muted text-xs" style={{ flex: 1 }}>
              {value.length} selected
            </span>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={selectAllVisible}
              disabled={filtered.length === 0}
            >
              Select all
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => onChange([])}
              disabled={value.length === 0}
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
